import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { TextBox, react } from "uikit";
import { useTranslation } from "react-i18next";

import useObjectEditor from "../../../../../../hooks/useObjectEditor";
import PaymentSystem from "../../../../../../services/PaymentSystem";
import {
	StyledColumn,
	StyledP,
	StyledRow,
} from "../../../../../../components/common";
import translationPath from "../../constants/translationPath";

import InternalController from "./Controller";

const EasyPayPaymentBase = react.withController<
	EasyPayPayment.PropsBase,
	EasyPayPayment.Controller
>(({ value, onChange, controller }) => {
	const { t } = useTranslation();
	const valueEditor = useObjectEditor(value, onChange);

	const [serviceIdError, setServiceIdError] = useState(false);

	const serviceId = valueEditor.useGetter("serviceId");
	const setServiceId = valueEditor.useSetter("serviceId");

	const minAmount = valueEditor.useGetter("minAmount");
	const setMinAmount = valueEditor.useSetter("minAmount");

	const maxAmount = valueEditor.useGetter("maxAmount");
	const setMaxAmount = valueEditor.useSetter("maxAmount");

	const textServiceId = useMemo(
		() =>
			t([`${translationPath}.modal.text.serviceId`, "ServiceId"]) || "",
		[t],
	);
	const textMinAmount = useMemo(
		() =>
			t([`${translationPath}.modal.text.minAmount`, "Мин. сумма"]) || "",
		[t],
	);
	const textMaxAmount = useMemo(
		() =>
			t([`${translationPath}.modal.text.maxAmount`, "Макс. сумма"]) || "",
		[t],
	);

	controller.setContext({
		serviceId,
		setServiceIdError,
	});

	const handleServiceId = useCallback(
		(next: string) => {
			setServiceId(next);
			setServiceIdError(false);
		},
		[setServiceId],
	);

	return (
		<StyledColumn gap="10px" w="100%">
			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px" flex={{ shrink: 0 }} text={{ ws: "normal" }}>
					{textServiceId} :
				</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 210px)" }}
					error={serviceIdError}
					value={serviceId || ""}
					onChange={handleServiceId}
					placeholder={
						t([
							`${translationPath}.modal.placeholders.serviceId`,
							"Номер сервиса EasyPay",
						]) || ""
					}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px" flex={{ shrink: 0 }} text={{ ws: "normal" }}>
					{textMinAmount} :
				</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 210px)" }}
					value={
						minAmount === undefined || minAmount === null
							? ""
							: String(minAmount)
					}
					onChange={(next) =>
						setMinAmount(next === "" ? undefined : Number(next))
					}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px" flex={{ shrink: 0 }} text={{ ws: "normal" }}>
					{textMaxAmount} :
				</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 210px)" }}
					value={
						maxAmount === undefined || maxAmount === null
							? ""
							: String(maxAmount)
					}
					onChange={(next) =>
						setMaxAmount(next === "" ? undefined : Number(next))
					}
				/>
			</StyledRow>
		</StyledColumn>
	);
}, InternalController);

const EasyPayPayment = memo(EasyPayPaymentBase);

declare namespace EasyPayPayment {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: PaymentSystem.Model;
		onChange: Dispatch<PaymentSystem.Model>;
	}
	type Props = PropsBase & RefAttributes<Ref>;
}

export default EasyPayPayment;
